'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useLayoutEffect, useRef } from 'react';
import { cn } from '@utils/cn';
import { getPrimaryBottomTabs, isRouteItemActive } from '@utils/route-utils';
import type { UserOrg } from '@utils/org-utils';
import { usePageChrome } from '@components/layout/page-chrome-context';
import AppAddCreateButton from '@components/AppAddCreateButton';
import SearchTrigger from '@components/search/SearchTrigger';
import { DockMenu } from './dock-menu';

export function BottomTabBar({
  organization_id,
  orgs = []
}: {
  organization_id: string;
  orgs?: UserOrg[];
}) {
  const t = useTranslations();
  const pathname = usePathname();
  const { chrome } = usePageChrome();
  const dockRef = useRef<HTMLDivElement>(null);
  const tabs = getPrimaryBottomTabs(organization_id, t);

  useLayoutEffect(() => {
    const dock = dockRef.current;
    if (!dock) return;
    const root = document.documentElement;
    const measure = () => {
      const height = dock.getBoundingClientRect().height;
      if (height > 0)
        root.style.setProperty('--mobile-dock-measured-height', `${height}px`);
    };
    measure();
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(measure);
    observer?.observe(dock);
    return () => {
      observer?.disconnect();
      root.style.removeProperty('--mobile-dock-measured-height');
    };
  }, [chrome?.hideMobileTabs]);

  if (chrome?.hideMobileTabs) return null;
  const tabClass =
    'mobile-dock-tab inline-flex min-w-11 flex-1 items-center justify-center rounded-selected text-secondary-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-text active:scale-[0.97]';

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30 px-5 pb-[var(--mobile-tab-island-offset)] desktop-chrome:hidden">
      <div
        ref={dockRef}
        className="mobile-dock-group relative mx-auto flex items-stretch gap-3"
      >
        <nav
          aria-label={t('app-chrome.primary-navigation')}
          className="mobile-dock navigation-glass pointer-events-auto flex min-w-0 flex-1 items-stretch rounded-dock"
        >
          {tabs.map(tab => {
            const active = isRouteItemActive(pathname, tab);
            return (
              <Link
                key={tab.id}
                href={tab.href}
                title={tab.label}
                aria-label={tab.label}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  tabClass,
                  active && 'bg-nav-selected text-brand-text'
                )}
              >
                {tab.icon ? (
                  <tab.icon className="mobile-dock-icon" aria-hidden />
                ) : null}
              </Link>
            );
          })}
          <DockMenu
            organization_id={organization_id}
            orgs={orgs}
            className={tabClass}
          />
        </nav>
        <div className="pointer-events-auto absolute bottom-[calc(100%+12px)] right-0">
          {chrome?.mobileDockAction ?? (
            <AppAddCreateButton
              organization_id={organization_id}
              variant="fab"
            />
          )}
        </div>
        <SearchTrigger className="mobile-search-island navigation-glass [&_svg]:size-6 pointer-events-auto inline-flex shrink-0 items-center justify-center rounded-full text-foreground transition-colors hover:text-brand-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-text active:scale-[0.97]" />
      </div>
    </div>
  );
}
